import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import { Trans } from 'react-i18next';
import TranslationText from "../../components/Translation/TranslationText.jsx";
import { SmartIdVerificationStep } from "../../account/SmartId/Shared/SmartIdVerificationStep.jsx";
import { PersonalCodeEntry } from "./PersonalCodeEntry.jsx";
import { RegistrationButtons } from "./RegistrationButtons.jsx";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import { SmartIdRegistrationService } from "./SmartIdRegistrationService.js";
import LegalEntityFunctions from "../../components/LegalEntityFunctions.jsx";
import * as constants from "../../constants/constants.jsx";

class SmartIdRegistrationPersonalCodeStep extends Component {
	constructor(props) {
		super(props);
		this.smartIdService = new SmartIdRegistrationService();
	}

	state = {
		personalCode: "",
		personalCodeError: "",
		isSmartIdVerifying: false,
		smartIdPin: "",
		smartIdPersonalInfo: null,
		confirmed: false,
		error: false,
		errorText: "",
		isProcessing: false,
		beginRecaptchaValidation: undefined
	};

	componentWillUnmount() {
		this.smartIdService.cancelSmartIdRegistration();
	}

	onPersonalCodeChange = (e) => {
		this.setState({
			personalCode: e.value,
			personalCodeError: "",
			error: false,
			errorText: "",
			beginRecaptchaValidation: undefined
		});
	};

	validatePersonalCodeFormatting = () => {
		const { personalCode } = this.state;
		const validation = this.smartIdService.validatePersonalCodeFormatting(personalCode);
		
		if (!validation.isValid) {
			this.setState({ personalCodeError: validation.errorMessage });
		}

		return validation.isValid;
	};

	startSmartIdRegistrationSession = async () => {
		const { isSmartIdVerifying, confirmed, personalCode } = this.state;
		
		if (isSmartIdVerifying || confirmed) {
			return;
		}

		this.smartIdService.resetPolling();
		
		const selectedCountry = LegalEntityFunctions.getLegalEntityId(localStorage.getItem(constants.SELECTED_COUNTRY));
		const result = await this.smartIdService.startSmartIdRegistrationSession(personalCode, selectedCountry);
		
		if (!result.success) {
			this.setState({
				error: true,
				errorText: result.error,
				isProcessing: false,
				beginRecaptchaValidation: undefined
			});
			// fast spamming safeguard
			this.smartIdService.cancelSmartIdRegistration();

			return;
		}

		this.setState({
			isSmartIdVerifying: true,
			smartIdPin: result.pin,
			isProcessing: false
		});

		// Notify parent component about verification state change
		if (this.props.onVerificationStateChange) {
			this.props.onVerificationStateChange(true);
		}

		await this.pollSmartIdRegistrationSession();
	};

	pollSmartIdRegistrationSession = async () => {
		const result = await this.smartIdService.pollSmartIdRegistrationSession();

		if (result.success) {
			this.setState({
				confirmed: true,
				isSmartIdVerifying: false,
				isProcessing: false,
				smartIdPersonalInfo: result.personalInfo
			});
		} else if (result.error) {
			this.setState({
				error: true,
				errorText: result.error,
				isSmartIdVerifying: false,
				isProcessing: false,
				beginRecaptchaValidation: undefined
			});

			// Notify parent component about verification state change
			if (this.props.onVerificationStateChange) {
				this.props.onVerificationStateChange(false);
			}
		} else if (result.continue) {
			// Continue polling
			setTimeout(this.pollSmartIdRegistrationSession, 3000);
		}
	};

	cancelSmartIdRegistration = () => {
		this.smartIdService.cancelSmartIdRegistration();
		this.setState({
			isSmartIdVerifying: false,
			isProcessing: false,
			smartIdPin: "",
			personalCodeError: "",
			error: false,
			errorText: "",
			confirmed: false,
			beginRecaptchaValidation: undefined
		});

		// Notify parent component about verification state change
		if (this.props.onVerificationStateChange) {
			this.props.onVerificationStateChange(false);
		}
	};

	beginRecaptchaValidation = () => {
		if (this.state.confirmed || this.state.isSmartIdVerifying) {
			return;
		}

		if (this.state.error) {
			this.smartIdService.resetPolling();
		}

		this.setState((prevState) => ({
			beginRecaptchaValidation: !prevState.beginRecaptchaValidation,
			error: false,
			errorText: ""
		}));
	}

	completeRecaptchaValidation = (recaptchaToken) => {
		this.setState({ isProcessing: false }, () => {
			this.confirm(recaptchaToken);
		});
	}

	confirm = async () => {
		if (this.state.confirmed || this.state.isSmartIdVerifying) {
			return;
		}

		this.setState({ isProcessing: true });

		const isPersonalCodeValid = this.validatePersonalCodeFormatting();
		if (!isPersonalCodeValid) {
			this.setState({ 
				isProcessing: false,
				beginRecaptchaValidation: undefined
			});
			return;
		}

		await this.startSmartIdRegistrationSession();
	}

	render() {
		const { confirmed, smartIdPersonalInfo } = this.state;

		if (confirmed) {
			const redirectState = {
				isSmartIdRegistration: true,
				personalInfo: smartIdPersonalInfo,
				previousPath: this.props.location.pathname
			};
			
			return (
				<Redirect to={{
					pathname: '/registration/NoAccount/SmartIdRegisterWithEmail',
					state: redirectState
				}} />
			);
		}

		return (
			<React.Fragment>
				{this.state.isSmartIdVerifying ? (
					<React.Fragment>
						<h1 className="register-smartid-title">
							<Trans i18nKey='smart_id_register_with_email_title' components={{ bold: <b /> }} />
						</h1>
						<p><TranslationText text="smart_id_verification_prompt_view_continue_app_message" /></p>
						<div className="smart-id-prompt-container page-container registration-smartid-flow">
							<SmartIdVerificationStep
								pin={this.state.smartIdPin}
								isWaiting={true}
								onCancel={this.cancelSmartIdRegistration}
								isRegistration={true}
								addCustomStyles={true}
								titleTextKey="smart_id_verification_prompt_view_code_sent_message"
							/>
						</div>
					</React.Fragment>
				) : (
					<React.Fragment>
						<PersonalCodeEntry
							personalCode={this.state.personalCode}
							personalCodeError={this.state.personalCodeError}
							errorText={this.state.errorText}
							onPersonalCodeChange={this.onPersonalCodeChange}
							consentText="create_new_account_page_smartid_consent_text"
						/>
						{
							this.state.beginRecaptchaValidation !== undefined
								? <GoogleReCaptcha onVerify={this.completeRecaptchaValidation} refreshReCaptcha={this.state.beginRecaptchaValidation} />
								: null
						}
						<RegistrationButtons
							isDisplayed={!this.state.isSmartIdVerifying && !this.state.confirmed}
							error={this.state.error}
							onBeginRecaptchaValidation={this.beginRecaptchaValidation}
							isProcessing={this.state.isProcessing}
							beginRecaptchaValidationState={this.state.beginRecaptchaValidation}
						/>
					</React.Fragment>
				)}
			</React.Fragment>
		);
	}
}

export default SmartIdRegistrationPersonalCodeStep; 